import { NOISE_GLSL } from './noise.glsl.js';

/**
 * The two GPU passes that turn a surface program into a PBR texture set.
 *
 *   pass 1 (MRT, half-float)  vUv -> [ albedoLinear.rgb | grimeMask ]
 *                                    [ ao | roughness | metalness | height ]
 *
 *   pass 2 (MRT, RGBA8)       reads the half-float pair and writes the three
 *                             final textures:
 *                               0: albedo  (SRGB8_ALPHA8, hardware-encoded)
 *                               1: ORM     (occlusion / roughness / metalness)
 *                               2: normal  (tangent space, Sobel of the height)
 *
 * Why two passes: the normal map has to be derived from the *finished* height
 * field with neighbourhood taps, and the same neighbourhood gives us cavity and
 * curvature for free. Those two signals drive the grime-in-crevices and
 * wear-on-edges response that separates a real surface from a noise texture.
 *
 * Colour space: surface programs author albedo perceptually (sRGB-ish numbers,
 * the way a texture artist thinks) and convert to linear at the end of pass 1,
 * so all the blending in pass 2 happens in linear light. The final albedo
 * attachment is an sRGB render target, so the hardware re-encodes on write and
 * decodes on sample — no manual conversion anywhere in the game shaders.
 */

export const QUAD_VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const PASS1_PREAMBLE = /* glsl */ `
precision highp float;
precision highp int;

in vec2 vUv;

layout(location = 0) out vec4 oAlbedo;
layout(location = 1) out vec4 oORMH;

uniform float uMacroTint;
uniform float uMacroRough;
uniform float uSeed;

${NOISE_GLSL}

struct Surf {
  vec3  albedo;  // perceptual / sRGB-ish authoring space
  float height;  // 0..1, drives the normal map and the cavity system
  float rough;
  float metal;
  float ao;
  float grime;   // 0..1 mask: where dirt is *allowed* to collect
};

vec3 sRGBToLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}
`;

const PASS1_MAIN = /* glsl */ `
void main() {
  Surf s;
  s.albedo = vec3(0.5);
  s.height = 0.5;
  s.rough  = 0.8;
  s.metal  = 0.0;
  s.ao     = 1.0;
  s.grime  = 0.0;

  surface(vUv, s);

  // Large-scale break-up (period 2 across the tile) so a repeated wall does not
  // read as an obvious grid: a slow tonal drift plus a matching roughness drift.
  float macro = fbmP(vUv * 2.0, 2.0, 3, uSeed + 913.0);
  float mrough = fbmP(vUv * 2.0, 2.0, 2, uSeed + 1777.0);
  s.albedo *= 1.0 + (macro - 0.5) * uMacroTint;
  s.rough   = s.rough + (mrough - 0.5) * uMacroRough;

  oAlbedo = vec4(sRGBToLinear(sat3(s.albedo)), sat(s.grime));
  oORMH   = vec4(sat(s.ao), clamp(s.rough, 0.015, 1.0), sat(s.metal), sat(s.height));
}
`;

/** Assemble the pass-1 program for one surface. */
export function pass1Fragment(surfaceGlsl) {
  return PASS1_PREAMBLE + surfaceGlsl + PASS1_MAIN;
}

export const RESOLVE_FRAG = /* glsl */ `
precision highp float;

in vec2 vUv;

layout(location = 0) out vec4 oAlbedo;
layout(location = 1) out vec4 oORM;
layout(location = 2) out vec4 oNormal;

uniform sampler2D tAlbedo;   // rgb = linear albedo, a = grime mask
uniform sampler2D tORMH;     // r = ao, g = roughness, b = metalness, a = height

uniform vec2  uTexel;        // 1 / size
uniform float uSobel;        // height -> slope gain, already resolution-scaled
uniform vec2  uCavityR;      // fine / broad cavity radius, in uv units
uniform float uCavityGain;
uniform float uCavityAO;
uniform vec3  uDirtColor;
uniform float uDirt;
uniform vec3  uWearColor;
uniform float uWear;
uniform float uWearMetal;    // 1 for metals: worn peaks go bare metal + glossy

float sat(float x) { return clamp(x, 0.0, 1.0); }
vec3  sat3(vec3 x) { return clamp(x, vec3(0.0), vec3(1.0)); }

float H(vec2 uv) { return texture(tORMH, uv).a; }

/** 8-tap ring average — wraps for free, the source uses RepeatWrapping. */
float ringAvg(vec2 uv, float r) {
  const float K = 0.70710678;
  float s = 0.0;
  s += H(uv + vec2( r, 0.0));
  s += H(uv + vec2(-r, 0.0));
  s += H(uv + vec2(0.0,  r));
  s += H(uv + vec2(0.0, -r));
  s += H(uv + vec2( r,  r) * K);
  s += H(uv + vec2(-r,  r) * K);
  s += H(uv + vec2( r, -r) * K);
  s += H(uv + vec2(-r, -r) * K);
  return s * 0.125;
}

void main() {
  vec2 uv = vUv;
  vec4 alb = texture(tAlbedo, uv);
  vec4 ormh = texture(tORMH, uv);

  vec3  albedo = alb.rgb;
  float grime  = alb.a;
  float ao     = ormh.r;
  float rough  = ormh.g;
  float metal  = ormh.b;
  float h      = ormh.a;

  // ---- Sobel normal (wrapped sampling keeps the tile seamless) -------------
  vec2 e = uTexel;
  float tl = H(uv + vec2(-e.x, -e.y));
  float tc = H(uv + vec2( 0.0, -e.y));
  float tr = H(uv + vec2( e.x, -e.y));
  float ml = H(uv + vec2(-e.x,  0.0));
  float mr = H(uv + vec2( e.x,  0.0));
  float bl = H(uv + vec2(-e.x,  e.y));
  float bc = H(uv + vec2( 0.0,  e.y));
  float br = H(uv + vec2( e.x,  e.y));

  float dx = (tl + 2.0 * ml + bl) - (tr + 2.0 * mr + br);
  float dy = (tl + 2.0 * tc + tr) - (bl + 2.0 * bc + br);
  vec3 n = normalize(vec3(dx * uSobel, dy * uSobel, 1.0));

  // ---- cavity + curvature -------------------------------------------------
  // Difference against a blurred height: negative in crevices, positive on the
  // ridges and chipped edges that stick out of the surface.
  float hFine  = ringAvg(uv, uCavityR.x);
  float hBroad = ringAvg(uv, uCavityR.y);

  float cavity = sat((hFine - h) * uCavityGain * 1.6 + (hBroad - h) * uCavityGain * 0.9);
  float peak   = sat((h - hFine) * uCavityGain * 1.8 + (h - hBroad) * uCavityGain * 0.6);
  // Local curvature: strong on thin ridges and chip edges, ~0 on flat ground.
  float curv   = sat((h * 8.0 - (tl + tc + tr + ml + mr + bl + bc + br)) * uCavityGain * 0.35);

  // ---- grime settles in the crevices --------------------------------------
  float dirtMask = sat(cavity * uDirt * (0.30 + 0.70 * grime) + grime * cavity * 0.35);
  albedo = mix(albedo, uDirtColor, dirtMask);
  rough  = mix(rough, 0.94, dirtMask * 0.75);
  metal  = mix(metal, 0.0, dirtMask * 0.85);   // dirt is a dielectric layer
  ao    *= 1.0 - sat(cavity * 1.2 + max(0.0, hBroad - h) * 2.0) * uCavityAO;

  // ---- wear exposes fresh material on the high-curvature peaks ------------
  float wearMask = sat((peak * 0.55 + curv * 0.85) * uWear * (0.35 + 0.65 * (1.0 - grime)));
  albedo = mix(albedo, uWearColor, wearMask);
  rough  = mix(rough, mix(0.60, 0.20, uWearMetal), wearMask);
  metal  = mix(metal, 1.0, wearMask * uWearMetal);

  oAlbedo = vec4(sat3(albedo), 1.0);
  oORM    = vec4(sat(ao), sat(rough), sat(metal), 1.0);
  oNormal = vec4(n * 0.5 + 0.5, 1.0);
}
`;
